import React, { useState, useEffect } from 'react';
import PartnerSidebar from '../components/Sidebar';
import { useSidebar } from '../context/SidebarContext';
import { useAuth } from '../context/AuthContext';
import { db } from '../config/firebase';
import { collection, query, where, onSnapshot, getDocs } from 'firebase/firestore';

function PartnerOverview() {
  const { isMobile, isOpen, toggleMobileSidebar } = useSidebar();
  const { currentUser } = useAuth();

  const [stats, setStats] = useState({
    totalQueries: 0,
    activeQueries: 0,
    completedQueries: 0,
    managers: 0
  });

  const [recentActivity, setRecentActivity] = useState([]);
  const [recentManagers, setRecentManagers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) return;

    const q = query(collection(db, 'queries'), where('partnerId', '==', currentUser.uid));
    const unsub = onSnapshot(q, (snapshot) => {
      const queries = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), _type: 'query' }));

      const active = queries.filter(item => {
        const s = (item.status || '').toLowerCase();
        return !['paid', 'completed', 'closed', 'rejected', 'cancelled'].includes(s);
      }).length;
      const completed = queries.filter(item => {
        const s = (item.status || '').toLowerCase();
        return ['paid', 'completed', 'closed'].includes(s);
      }).length;

      queries.sort((a, b) => {
        const dateA = a.updatedAt?.toDate ? a.updatedAt.toDate() : new Date(a.updatedAt || a.createdAt || 0);
        const dateB = b.updatedAt?.toDate ? b.updatedAt.toDate() : new Date(b.updatedAt || b.createdAt || 0);
        return dateB - dateA;
      });

      setStats(prev => ({ ...prev, totalQueries: queries.length, activeQueries: active, completedQueries: completed }));
      setRecentActivity(queries.slice(0, 8));
      setLoading(false);
    }, (error) => {
      console.error("Error fetching queries:", error);
      setLoading(false);
    });

    return () => unsub();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;

    const fetchManagers = async () => {
      try {
        const managersQ = query(collection(db, 'managers'), where('partnerId', '==', currentUser.uid));
        const managersSnapshot = await getDocs(managersQ);
        const managers = managersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), _type: 'manager' }));
        managers.sort((a, b) => {
          const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
          const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
          return dateB - dateA;
        });
        setStats(prev => ({ ...prev, managers: managers.length }));
        setRecentManagers(managers.slice(0, 5));
      } catch (error) {
        console.error("Error fetching managers:", error);
      }
    };

    fetchManagers();
  }, [currentUser]);

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diff = (now - date) / 1000;
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)} mins ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
    return `${Math.floor(diff / 86400)} days ago`;
  };

  const getStatusColor = (status) => {
    const s = (status || '').toLowerCase();
    if (s.includes('pending')) return 'text-yellow-600 bg-yellow-50';
    if (s.includes('completed') || s.includes('paid')) return 'text-green-600 bg-green-50';
    if (s.includes('reject')) return 'text-red-600 bg-red-50';
    return 'text-blue-600 bg-blue-50';
  };

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <PartnerSidebar />
      <div
        className="flex-1 overflow-y-auto transition-all duration-300"
        style={{ marginLeft: isMobile ? 0 : (isOpen ? '16rem' : '5rem') }}
      >
        <div className="p-6 min-h-screen">
          <div className="mb-6">
            <div className="flex items-center">
              {isMobile && (
                <button onClick={toggleMobileSidebar} className="mr-4 p-2 rounded-lg bg-white border border-gray-200 text-gray-600 hover:text-brand-golden">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              )}
              <div>
                <h1 className="text-3xl font-bold text-brand-dark">Partner Dashboard</h1>
                <p className="text-gray-600 mt-1">Overview of your service requests and team.</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-brand-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Queries</p>
                  <h3 className="text-2xl font-bold text-brand-dark mt-1">{stats.totalQueries}</h3>
                </div>
                <div className="bg-brand-golden/10 p-3 rounded-lg">
                  <svg className="w-6 h-6 text-brand-golden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-brand-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Active Queries</p>
                  <h3 className="text-2xl font-bold text-blue-600 mt-1">{stats.activeQueries}</h3>
                </div>
                <div className="bg-blue-100 p-3 rounded-lg">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-brand-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Completed</p>
                  <h3 className="text-2xl font-bold text-green-600 mt-1">{stats.completedQueries}</h3>
                </div>
                <div className="bg-green-100 p-3 rounded-lg">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-brand-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Team Members</p>
                  <h3 className="text-2xl font-bold text-purple-600 mt-1">{stats.managers}</h3>
                </div>
                <div className="bg-purple-100 p-3 rounded-lg">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-brand-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-brand-dark">Recent Queries</h2>
                <span className="text-sm text-gray-500">{recentActivity.length} recent</span>
              </div>
              <div className="space-y-3">
                {loading ? (
                  <p className="text-gray-500 text-center py-6">Loading...</p>
                ) : recentActivity.length > 0 ? (
                  recentActivity.map((item) => (
                    <div key={item.id} className="flex items-center space-x-4 p-3 hover:bg-gray-50 rounded-lg transition-colors">
                      <div className="bg-blue-100 p-2.5 rounded-lg flex-shrink-0">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-brand-dark truncate">{item.title || item.serviceType || 'Service Request'}</p>
                        <p className="text-sm text-gray-500 truncate">{item.location || item.address || 'No location'}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <span className={`text-xs font-semibold px-2 py-1 rounded-full ${getStatusColor(item.status)}`}>
                          {item.status || 'Pending'}
                        </span>
                        <p className="text-xs text-gray-400 mt-1">{formatTime(item.updatedAt || item.createdAt)}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-6">No queries yet. Create your first service request!</p>
                )}
              </div>
            </div>

            <div className="bg-brand-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-brand-dark">Team Members</h2>
                <span className="text-sm text-gray-500">{stats.managers} total</span>
              </div>
              <div className="space-y-3">
                {loading ? (
                  <p className="text-gray-500 text-center py-6">Loading...</p>
                ) : recentManagers.length > 0 ? (
                  recentManagers.map((manager) => (
                    <div key={manager.id} className="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-colors">
                      <div className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-purple-600 font-semibold text-sm">
                          {(manager.name || manager.fullName || 'M').charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-brand-dark truncate">{manager.name || manager.fullName || 'Manager'}</p>
                        <p className="text-sm text-gray-500 truncate">{manager.email || 'No email'}</p>
                      </div>
                      <span className={`text-xs font-semibold px-2 py-1 rounded-full flex-shrink-0 ${manager.isActive !== false ? 'text-green-600 bg-green-50' : 'text-gray-500 bg-gray-100'}`}>
                        {manager.isActive !== false ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-6">No team members yet.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PartnerOverview;
